import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { cloneDeep } from 'es-toolkit';

import { OVERCHARGE_CLAIMS_FILTERS_INITIAL_DATA } from '../../../../contants/ClaimsEnums';
import { SEARCH_VIEW_TYPES } from '../../../../contants/common';
import {
  setClaimsOverChargeFilters,
  setOverChargeClaimsEditDrawer,
  setSelectedOverChargeClaims,
} from '../../../../redux/Claims/OverCharge/OverChargeActions';
import EmptyRecords from '../../../components/Common/EmptyRecords';
import ScrollPagination from '../../../components/Paginations/ScrollPagination';
import Paper from '../../../components/Paper/Paper';
import ContentSpinner from '../../../components/Spinners/ContentSpinner';
import OverChargeEditDrawer from './Components/Edit/Drawer/OverChargeEditDrawer';
import OverChargeCard from './Components/OverChargeCard';
import { useDeleteOCClaims, useGetOCClaimsList } from './hooks';
import OCClaimTableView from './List/OCClaimTableView';

const OverChargeClaimsContent = () => {
  const dispatch = useDispatch();
  const { state: locationState } = useLocation();
  const selectedCompany = useSelector((state) => state.auth.selectedCompany);
  const { claimsFilters, selectedClaims, claimViewMode } = useSelector((state) => state.overChargeClaims);
  const { ...searchFilters } = claimsFilters;
  const [activeClaimKey, setActiveClaimKey] = useState();
  const handledNavigationClaimKeyRef = useRef(null);

  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, isLoading } = useGetOCClaimsList({
    companyId: selectedCompany?.companyId,
    enabled: Boolean(selectedCompany?.companyId),
    searchFilters,
  });

  const { mutate: deleteClaims } = useDeleteOCClaims();

  const handleCardClick = (fbocKey) => {
    setActiveClaimKey(fbocKey);
    dispatch(setOverChargeClaimsEditDrawer(true));
  };

  const hasRows = (data?.flatClaims?.length ?? 0) > 0;
  const showInitialSkeleton = (isLoading || isFetching) && !hasRows;
  const showEmptyState = !isLoading && !isFetching && !hasRows;

  const claimsList = useMemo(() => data?.flatClaims ?? [], [data?.flatClaims]);

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
      return;
    }
  };

  const onSelect = (id, remove) => {
    const selectedClaimsLocal = cloneDeep(selectedClaims);
    if (!remove) {
      selectedClaimsLocal.push(id);
    } else {
      const index = selectedClaimsLocal.indexOf(id);
      selectedClaimsLocal.splice(index, 1);
    }
    dispatch(setSelectedOverChargeClaims(selectedClaimsLocal));
  };

  const handleDelete = (fbocKey) => {
    deleteClaims({
      fbocKeys: String(fbocKey),
      filters: claimsFilters,
      onSuccess: () => dispatch(setSelectedOverChargeClaims(selectedClaims.filter((claimId) => claimId !== fbocKey))),
    });
  };

  useEffect(() => {
    if (selectedCompany?.companyId) {
      dispatch(
        setClaimsOverChargeFilters({
          ...OVERCHARGE_CLAIMS_FILTERS_INITIAL_DATA,
          pageNo: 1,
        }),
      );
    }
  }, [selectedCompany?.companyId, dispatch]);

  useEffect(() => {
    const navigationClaimKey = Number(locationState?.showEmailPromptForFbOcKey);

    if (navigationClaimKey > 0 && handledNavigationClaimKeyRef.current !== navigationClaimKey) {
      handledNavigationClaimKeyRef.current = navigationClaimKey;
      setActiveClaimKey(navigationClaimKey);
      dispatch(setOverChargeClaimsEditDrawer(true));
    }
  }, [dispatch, locationState?.showEmailPromptForFbOcKey]);

  const isCardView = useMemo(() => claimViewMode === SEARCH_VIEW_TYPES.CARD, [claimViewMode]);

  return (
    <div className="py-15 h-100">
      {isCardView ? (
        showInitialSkeleton ? (
          <Paper className="h-100 w-100 mb-15">
            <ContentSpinner />
          </Paper>
        ) : (
          <ScrollPagination
            className={`${claimsList?.length > 0 ? 'mb-15' : 'pb-15'} h-100 `}
            loadMore={handleLoadMore}
            hasMore={isFetchingNextPage ? false : hasNextPage}
            isLoading={isFetchingNextPage}
            scrollOffset={50}
          >
            {claimsList?.length > 0
              ? claimsList.map((row) => (
                  <div className="mb-15" key={row.fbocKey}>
                    <OverChargeCard
                      data={row}
                      onSelect={onSelect}
                      handleCardClick={handleCardClick}
                      isSelected={selectedClaims.includes(row.fbocKey)}
                      handleDelete={handleDelete}
                    />
                  </div>
                ))
              : showEmptyState && <EmptyRecords />}
          </ScrollPagination>
        )
      ) : (
        <OCClaimTableView
          handleLoadMore={handleLoadMore}
          isLoading={isFetchingNextPage}
          hasMore={isFetchingNextPage ? false : hasNextPage}
          claimsList={claimsList}
          handleCardClick={handleCardClick}
          onSelect={onSelect}
          selectedClaims={selectedClaims}
          handleDelete={handleDelete}
          isFirstTime={showInitialSkeleton}
        />
      )}
      <OverChargeEditDrawer claimKey={activeClaimKey} onClaimKeyReset={() => setActiveClaimKey(undefined)} />
    </div>
  );
};

export default OverChargeClaimsContent;
